/**
 * Tabla del Admin (design-system §7 · Tabla): fila 44 px, texto 14 px, encabezado 12 px/500 en
 * mayúsculas con letter-spacing, divisores suaves, hover de fila y cabecera sticky. Las columnas
 * numéricas y de hora usan `numeric` (tabular-nums, alineadas a la derecha).
 */
import { cn } from '../../lib/cn';

import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto rounded-fm-lg border border-fm-border bg-fm-surface">
      <table
        className={cn('w-full border-collapse text-fm-sm text-fm-text', className)}
        {...props}
      />
    </div>
  );
}

export function TableHead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('sticky top-0 z-10 bg-fm-surface', className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={className} {...props} />;
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
}

export function TableRow({ className, selected, ...props }: TableRowProps) {
  return (
    <tr
      aria-selected={selected || undefined}
      className={cn(
        'h-fm-touch border-b border-fm-border last:border-b-0 transition-colors duration-fm-fast',
        'hover:bg-fm-bg-sunken motion-reduce:transition-none',
        selected && 'bg-fm-info-soft',
        className,
      )}
      {...props}
    />
  );
}

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export function TableHeaderCell({ className, numeric, ...props }: TableHeaderCellProps) {
  return (
    <th
      scope="col"
      className={cn(
        'h-fm-touch border-b border-fm-border px-fm-3 text-fm-xs font-medium uppercase tracking-[0.04em] text-fm-text-secondary',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
      {...props}
    />
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

export function TableCell({ className, numeric, ...props }: TableCellProps) {
  return (
    <td
      className={cn('px-fm-3 align-middle', numeric && 'text-right fm-tabular', className)}
      {...props}
    />
  );
}

export function TableCaption({ className, ...props }: HTMLAttributes<HTMLTableCaptionElement>) {
  return <caption className={cn('sr-only', className)} {...props} />;
}
